import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { API_URL } from '../utils/Functions';

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const defaultForm = {
    title: '', slug: '', description: '', image: '', page_id: '', sort_order: 0, is_active: true
  };
  const [formData, setFormData] = useState(defaultForm);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' };
      const [sRes, pRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/services`, { headers }),
        fetch(`${API_URL}/api/admin/pages`, { headers })
      ]);
      if (!sRes.ok) throw new Error('Failed to load services');
      const sData = await sRes.json();
      setServices(sData.data || []);
      if (pRes.ok) {
        const pJson = await pRes.json();
        setPages(pJson.data?.data || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleOpenCreate = () => {
    setFormData(defaultForm);
    setImageFile(null);
    setImagePreview(null);
    setIsEditing(false);
    setCurrentId(null);
    setSlugManuallyEdited(false);
    setShowModal(true);
  };

  const handleOpenEdit = (service) => {
    setFormData({
      title: service.title || '', slug: service.slug || '',
      description: service.description || '', image: service.image || '',
      page_id: service.page_id || '', sort_order: service.sort_order || 0,
      is_active: !!service.is_active,
    });
    setImageFile(null);
    setImagePreview(service.image || null);
    setIsEditing(true);
    setCurrentId(service.id);
    setSlugManuallyEdited(true); // In edit mode, don't override existing slug
    setShowModal(true);
  };

  const confirmDelete = (service) => {
    setItemToDelete(service);
  };

  const executeDelete = async () => {
    if (!itemToDelete) return;
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/admin/services/${itemToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!res.ok) throw new Error();
      setServices(services.filter(s => s.id !== itemToDelete.id));
      setItemToDelete(null);
      toast.success('Service deleted successfully!');
    } catch { toast.error('Error deleting service.'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const url = isEditing ? `${API_URL}/api/admin/services/${currentId}` : `${API_URL}/api/admin/services`;
      const payload = new FormData();
      payload.append('title', formData.title);
      payload.append('slug', formData.slug);
      payload.append('description', formData.description || '');
      payload.append('page_id', formData.page_id || '');
      payload.append('sort_order', formData.sort_order);
      payload.append('is_active', formData.is_active ? 1 : 0);
      if (imageFile) payload.append('image', imageFile);
      if (isEditing) payload.append('_method', 'PUT');

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
        body: payload
      });
      const data = await res.json();
      if (!res.ok) throw data;
      setShowModal(false);
      fetchData();
      toast.success(isEditing ? 'Service updated successfully!' : 'Service created successfully!');
    } catch (err) {
      toast.error(err.message || 'Error saving service.');
    } finally {
      setSaving(false);
    }
  };

  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const toSlug = (str) => str.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'slug') {
      setSlugManuallyEdited(true);
      setFormData(prev => ({ ...prev, slug: value }));
    } else if (name === 'title') {
      setFormData(prev => ({
        ...prev,
        title: value,
        // Auto-fill slug only if user hasn't manually edited it
        ...((!slugManuallyEdited) && { slug: toSlug(value) })
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const linkedPage = (pageId) => pages.find(p => p.id === parseInt(pageId));

  const filteredServices = services.filter(service => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const page = linkedPage(service.page_id);
    return (
      (service.title && service.title.toLowerCase().includes(term)) ||
      (service.description && service.description.toLowerCase().includes(term)) ||
      (service.slug && service.slug.toLowerCase().includes(term)) ||
      (page && page.title.toLowerCase().includes(term))
    );
  });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        .sv-wrap { font-family: 'Inter', sans-serif; }

        /* Hero Banner */
        .sv-hero {
          background: linear-gradient(135deg, #040f1f 0%, #061830 40%, #CFA047 100%);
          border-radius: 18px; padding: 32px 36px; margin-bottom: 28px;
          display: flex; align-items: center; justify-content: space-between;
          position: relative; overflow: hidden;
        }
        .sv-hero::before {
          content: ''; position: absolute; top: -40px; right: -40px;
          width: 240px; height: 240px; border-radius: 50%;
          background: rgba(255,255,255,0.05);
        }
        .sv-hero::after {
          content: ''; position: absolute; bottom: -60px; right: 120px;
          width: 180px; height: 180px; border-radius: 50%;
          background: rgba(255,255,255,0.04);
        }
        .sv-hero-left { position: relative; z-index: 1; }
        .sv-hero-eyebrow { font-size: 11px; font-weight: 700; color: #CFA047; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
        .sv-hero-title { font-size: 26px; font-weight: 800; color: #ffffff; margin: 0 0 8px; line-height: 1.2; }
        .sv-hero-sub { font-size: 14px; color: rgba(255,255,255,0.8); margin: 0 0 22px; }
        .sv-hero-right { position: relative; z-index: 1; display: flex; gap: 16px; }
        .sv-hero-stat { background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.15); border-radius: 14px; padding: 16px 22px; text-align: center; backdrop-filter: blur(6px); }
        .sv-hero-stat-val { font-size: 28px; font-weight: 800; color: #ffffff; line-height: 1; }
        .sv-hero-stat-label { font-size: 11px; color: #CFA047; font-weight: 600; margin-top: 4px; }

        .sv-create-btn {
          display: flex; align-items: center; gap: 8px;
          padding: 11px 22px; border-radius: 10px; border: none;
          background: #ffffff;
          color: #061830; font-size: 14px; font-weight: 700;
          cursor: pointer; font-family: 'Inter', sans-serif;
          box-shadow: 0 4px 14px rgba(0,0,0,0.15); transition: all 0.2s;
          white-space: nowrap; flex-shrink: 0;
        }
        .sv-create-btn:hover { box-shadow: 0 6px 18px rgba(207, 160, 71,0.4); transform: translateY(-1px); }

        /* Error */
        .sv-error { background: #fff1f2; border: 1px solid #fecdd3; border-radius: 10px; padding: 12px 16px; color: #e11d48; font-size: 13px; margin-bottom: 20px; display: flex; align-items: center; gap: 10px; }

        /* Stats */
        .sv-stats { display: flex; gap: 16px; margin-bottom: 24px; }
        .sv-stat { flex: 1; background: #fff; border-radius: 12px; border: 1px solid #dde3ea; padding: 16px 20px; box-shadow: 0 1px 4px rgba(0,0,0,0.04); }
        .sv-stat-val { font-size: 28px; font-weight: 700; color: #1a2332; line-height: 1; }
        .sv-stat-label { font-size: 12px; color: #8496a9; font-weight: 500; margin-top: 4px; }

        /* Table card */
        .sv-card { background: #fff; border-radius: 14px; border: 1px solid #dde3ea; box-shadow: 0 2px 12px rgba(0,0,0,0.05); overflow: hidden; }
        .sv-toolbar { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid #eef1f5; }
        .sv-toolbar-title { font-size: 14px; font-weight: 600; color: #2d3a4b; }
        .sv-count { background: #f0f4f8; color: #5a6a7e; font-size: 12px; font-weight: 600; padding: 3px 10px; border-radius: 20px; margin-left: 8px; }

        table.sv-table { width: 100%; border-collapse: collapse; }
        .sv-table thead tr { background: #f7f9fc; }
        .sv-table th { padding: 13px 18px; font-size: 11.5px; font-weight: 700; color: #8496a9; text-transform: uppercase; letter-spacing: 0.6px; border-bottom: 1px solid #eef1f5; white-space: nowrap; }
        .sv-table td { padding: 15px 18px; font-size: 13.5px; color: #3d4f62; border-bottom: 1px solid #f0f4f8; vertical-align: middle; }
        .sv-table tbody tr:last-child td { border-bottom: none; }
        .sv-table tbody tr:hover td { background: #f7f9fc; }
        
        .sv-card-img { width: 100%; height: 160px; object-fit: cover; }
        .sv-card-img-placeholder { width: 100%; height: 160px; background: #f0f4f8; display: flex; align-items: center; justify-content: center; color: #cbd5e1; font-size: 40px; }
        .sv-card-body { padding: 16px; }
        .sv-card-name { font-weight: 700; color: #1a2332; font-size: 16px; margin-bottom: 8px; }
        .sv-card-desc { font-size: 13px; color: #64748b; line-height: 1.5; margin-bottom: 12px; }
        .sv-page-link { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: #CFA047; font-weight: 600; margin-bottom: 12px; }
        
        .sv-card-footer { padding: 12px 16px; border-top: 1px solid #f1f5f9; display: flex; align-items: center; justify-content: space-between; }
        .sv-badge { display: inline-flex; align-items: center; gap: 5px; padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; }
        .sv-badge.active { background: #f8fafc; color: #061830; }
        .sv-badge.inactive { background: #f1f5f9; color: #64748b; }
        .sv-badge-dot { width: 6px; height: 6px; border-radius: 50%; }
        .active .sv-badge-dot { background: #CFA047; }

        /* Card grid */
        .sv-grid-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 20px; padding: 24px; }
        .sv-service-card { background: #fff; border-radius: 14px; border: 1px solid #dde3ea; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.05); transition: all 0.22s; display: flex; flex-direction: column; }
        .sv-service-card:hover { box-shadow: 0 8px 24px rgba(0,0,0,0.1); transform: translateY(-3px); border-color: #CFA047; }
        .sv-card-actions { display: flex; gap: 6px; }

        .sv-actions { display: flex; gap: 6px; justify-content: flex-end; }
        .sv-act-btn { display: flex; align-items: center; justify-content: center; width: 34px; height: 34px; border-radius: 8px; border: 1.5px solid #dde3ea; background: #f7f9fc; color: #6b7a90; cursor: pointer; font-size: 13px; transition: all 0.18s; }
        .sv-act-btn:hover { background: #fff; border-color: #bcc7d4; color: #1a2332; box-shadow: 0 2px 6px rgba(0,0,0,0.08); }
        .sv-act-btn.edit:hover { border-color: #CFA047; color: #CFA047; background: #f8fafc; }
        .sv-act-btn.del:hover { border-color: #fca5a5; color: #ef4444; background: #fff1f2; }

        /* Section label */
        .sv-section-label { font-size: 13px; font-weight: 700; color: #5a6a7e; margin-bottom: 16px; display: flex; align-items: center; gap: 8px; }
        .sv-section-label::after { content: ''; flex: 1; height: 1px; background: #eef1f5; }

        /* Toolbar/Search */
        .sv-toolbar-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; flex-wrap: wrap; gap: 12px; }
        .sv-toolbar-header .sv-section-label { margin-bottom: 0; flex: 1; min-width: 200px; }
        .sv-search-box { position: relative; width: 280px; }
        .sv-search-box i { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #9babbf; font-size: 13px; }
        .sv-search-box input { width: 100%; padding: 10px 14px 10px 38px; border-radius: 10px; border: 1.5px solid #dde3ea; font-size: 13px; font-family: 'Inter', sans-serif; color: #2d3a4b; outline: none; transition: all 0.2s; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.02); }
        .sv-search-box input:focus { border-color: #CFA047; box-shadow: 0 0 0 3px rgba(207, 160, 71,0.12); }

        /* Empty state */
        .sv-empty { text-align: center; padding: 70px 24px; }
        .sv-empty-icon-wrap { width: 88px; height: 88px; border-radius: 24px; background: linear-gradient(135deg, #f8fafc, #fdf5e6); display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; box-shadow: 0 8px 24px rgba(207, 160, 71,0.15); }
        .sv-empty-icon-wrap i { font-size: 38px; color: #CFA047; }
        .sv-empty-title { font-size: 18px; font-weight: 700; color: #2d3a4b; margin-bottom: 8px; }
        .sv-empty-text { font-size: 14px; color: #9babbf; max-width: 300px; margin: 0 auto 24px; line-height: 1.6; }
        .sv-empty-btn { display: inline-flex; align-items: center; gap: 8px; padding: 11px 22px; border-radius: 10px; border: none; background: linear-gradient(135deg, #CFA047, #061830); color: white; font-size: 14px; font-weight: 600; cursor: pointer; font-family: 'Inter', sans-serif; box-shadow: 0 4px 12px rgba(207, 160, 71,0.3); transition: all 0.2s; }
        .sv-empty-btn:hover { box-shadow: 0 6px 18px rgba(207, 160, 71,0.4); transform: translateY(-1px); }

        /* Stats hidden, skeletons for cards */
        .sv-skel-card { border-radius: 14px; border: 1px solid #dde3ea; overflow: hidden; }
        .sv-skel-img { height: 170px; background: linear-gradient(90deg, #f0f4f8 25%, #e4eaf1 50%, #f0f4f8 75%); background-size: 200% 100%; animation: sv-shimmer 1.4s infinite; }
        .sv-skel-body { padding: 16px 18px; display: flex; flex-direction: column; gap: 10px; }
        .sv-skel { height: 14px; border-radius: 6px; background: linear-gradient(90deg, #f0f4f8 25%, #e4eaf1 50%, #f0f4f8 75%); background-size: 200% 100%; animation: sv-shimmer 1.4s infinite; }
        @keyframes sv-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

        /* ── Slide-in Drawer ── */
        .sv-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.4); backdrop-filter: blur(5px); z-index: 1000; display: flex; justify-content: flex-end; }
        .sv-drawer { width: 100%; max-width: 780px; height: 100vh; background: #fff; display: flex; flex-direction: column; box-shadow: -20px 0 60px rgba(0,0,0,0.14); animation: sv-slide 0.28s cubic-bezier(0.16,1,0.3,1); }
        @keyframes sv-slide { from { transform: translateX(100%); } to { transform: translateX(0); } }

        /* Drawer Header */
        .sv-dh { display: flex; align-items: center; justify-content: space-between; padding: 0 28px; height: 64px; border-bottom: 1px solid #eef1f5; flex-shrink: 0; background: #fff; }
        .sv-dh-left { display: flex; align-items: center; gap: 14px; }
        .sv-dh-icon { width: 40px; height: 40px; border-radius: 12px; background: linear-gradient(135deg, #fdf5e6, #faedcf); display: flex; align-items: center; justify-content: center; color: #CFA047; font-size: 16px; }
        .sv-dh-title { font-size: 17px; font-weight: 700; color: #1a2332; margin: 0; }
        .sv-dh-sub { font-size: 12px; color: #9babbf; margin: 2px 0 0; }
        .sv-dh-actions { display: flex; align-items: center; gap: 10px; }
        .sv-close { background: #f0f4f8; border: none; width: 36px; height: 36px; border-radius: 9px; cursor: pointer; color: #6b7a90; font-size: 15px; transition: all 0.18s; display: flex; align-items: center; justify-content: center; }
        .sv-close:hover { background: #fee2e2; color: #ef4444; }

        /* Drawer Body: two-panel layout */
        .sv-drawer-body { display: flex; flex: 1; overflow: hidden; }

        /* Left: image preview panel */
        .sv-img-panel { width: 280px; flex-shrink: 0; background: #f7f9fc; border-right: 1px solid #eef1f5; display: flex; flex-direction: column; overflow: hidden; }
        .sv-img-banner { flex: 1; position: relative; display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .sv-img-banner img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .sv-img-banner-placeholder { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; color: #cbd5e1; padding: 20px; text-align: center; height: 100%; }
        .sv-img-banner-placeholder i { font-size: 52px; color: #faedcf; }
        .sv-img-banner-placeholder span { font-size: 13px; color: #9babbf; line-height: 1.5; }
        .sv-upload-overlay { position: absolute; inset: 0; background: rgba(207, 160, 71,0.88); display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; opacity: 0; transition: opacity 0.2s; cursor: pointer; }
        .sv-img-banner:hover .sv-upload-overlay { opacity: 1; }
        .sv-upload-overlay i { font-size: 28px; color: white; }
        .sv-upload-overlay span { font-size: 13px; color: white; font-weight: 600; }
        .sv-upload-overlay input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
        .sv-img-info { padding: 14px 16px; border-top: 1px solid #eef1f5; }
        .sv-img-info-title { font-size: 11px; font-weight: 700; color: #8496a9; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
        .sv-toggle-row { display: flex; align-items: center; justify-content: space-between; }
        .sv-toggle-label { font-size: 13px; font-weight: 500; color: #3d4f62; }
        .sv-switch { position: relative; width: 42px; height: 24px; flex-shrink: 0; }
        .sv-switch input { opacity: 0; width: 0; height: 0; }
        .sv-slider { position: absolute; inset: 0; background: #dde3ea; border-radius: 24px; cursor: pointer; transition: 0.2s; }
        .sv-slider:before { content: ''; position: absolute; width: 18px; height: 18px; left: 3px; top: 3px; background: white; border-radius: 50%; transition: 0.2s; box-shadow: 0 1px 4px rgba(0,0,0,0.18); }
        .sv-switch input:checked + .sv-slider { background: #CFA047; }
        .sv-switch input:checked + .sv-slider:before { transform: translateX(18px); }

        /* Right: form panel */
        .sv-form-panel { flex: 1; overflow-y: auto; padding: 28px; display: flex; flex-direction: column; gap: 22px; }

        /* Section header */
        .sv-section-title { font-size: 11px; font-weight: 700; color: #CFA047; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 14px; display: flex; align-items: center; gap: 8px; }
        .sv-section-title::after { content: ''; flex: 1; height: 1px; background: #fdf5e6; }

        /* Floating label fields */
        .sv-float-group { position: relative; margin-bottom: 6px; }
        .sv-float-inp { width: 100%; padding: 20px 16px 8px; border-radius: 10px; border: 1.5px solid #dde3ea; font-size: 14px; font-family: 'Inter', sans-serif; color: #2d3a4b; outline: none; background: #f9fbfc; transition: border-color 0.18s, box-shadow 0.18s; box-sizing: border-box; }
        .sv-float-inp:focus { border-color: #CFA047; box-shadow: 0 0 0 3px rgba(207, 160, 71,0.1); background: #fff; }
        .sv-float-inp:focus + .sv-float-label, .sv-float-inp:not(:placeholder-shown) + .sv-float-label { top: 7px; font-size: 10.5px; color: #CFA047; font-weight: 700; }
        .sv-float-label { position: absolute; top: 14px; left: 16px; font-size: 13px; color: #9babbf; font-weight: 500; pointer-events: none; transition: all 0.18s; }
        .sv-float-textarea { min-height: 100px; resize: vertical; line-height: 1.6; padding-top: 22px; }
        .sv-float-select { padding: 20px 36px 8px 16px; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238496a9' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 14px center; background-color: #f9fbfc; }
        .sv-float-select:focus { border-color: #CFA047; box-shadow: 0 0 0 3px rgba(207, 160, 71,0.1); background-color: #fff; }

        .sv-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .sv-hint { font-size: 11.5px; color: #9babbf; margin-top: 4px; padding-left: 4px; }

        /* Drawer Footer */
        .sv-df { padding: 18px 28px; border-top: 1px solid #eef1f5; display: flex; justify-content: space-between; align-items: center; background: #f9fbfc; flex-shrink: 0; }
        .sv-df-hint { font-size: 12px; color: #9babbf; }
        .sv-df-btns { display: flex; gap: 12px; }
        .sv-btn-cancel { padding: 10px 22px; border-radius: 9px; font-weight: 600; cursor: pointer; background: white; border: 1.5px solid #dde3ea; color: #4a5568; font-family: 'Inter', sans-serif; font-size: 13px; transition: all 0.18s; }
        .sv-btn-cancel:hover { background: #f7f9fc; border-color: #bcc7d4; }
        .sv-btn-save { padding: 10px 26px; border-radius: 9px; font-weight: 600; cursor: pointer; background: linear-gradient(135deg, #CFA047 0%, #061830 100%); border: none; color: white; font-family: 'Inter', sans-serif; font-size: 13px; box-shadow: 0 2px 8px rgba(207, 160, 71,0.3); transition: all 0.18s; display: flex; align-items: center; gap: 8px; }
        .sv-btn-save:hover { box-shadow: 0 4px 14px rgba(207, 160, 71,0.4); transform: translateY(-1px); }
        .sv-btn-save:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
      `}</style>

      <div className="sv-wrap">
        <div className="sv-hero">
          <div className="sv-hero-left">
            <div className="sv-hero-eyebrow"><i className="fa-solid fa-briefcase"></i> Service Management</div>
            <h2 className="sv-hero-title">Your Services Catalog</h2>
            <p className="sv-hero-sub">Create, manage and showcase the services you offer to your clients.</p>
          </div>
          <div className="sv-hero-right">
            <button className="sv-create-btn" onClick={handleOpenCreate}>
              <i className="fa-solid fa-plus"></i>
              Add New Service
            </button>
          </div>
        </div>

        {error && <div className="sv-error"><i className="fa-solid fa-circle-exclamation"></i>{error}</div>}

        <div className="sv-toolbar-header">
          <div className="sv-section-label">
            <i className="fa-solid fa-grip" style={{ color: '#CFA047' }}></i>
            All Services
            <span className="sv-count" style={{ marginLeft: 0 }}>{filteredServices.length}</span>
          </div>
          <div className="sv-search-box">
            <i className="fa-solid fa-magnifying-glass"></i>
            <input 
              type="text" 
              placeholder="Search services by title, description..." 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
            />
          </div>
        </div>

          {loading ? (
            <div className="sv-grid-list">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="sv-skel-card">
                  <div className="sv-skel-img"></div>
                  <div className="sv-skel-body">
                    <div className="sv-skel" style={{ width: '70%' }}></div>
                    <div className="sv-skel" style={{ width: '90%' }}></div>
                    <div className="sv-skel" style={{ width: '50%' }}></div>
                  </div>
                </div>
              ))}
            </div>
          ) : services.length === 0 ? (
            <div className="sv-empty">
              <div className="sv-empty-icon-wrap"><i className="fa-solid fa-briefcase"></i></div>
              <div className="sv-empty-title">No services added yet</div>
              <div className="sv-empty-text">Start building your services catalog. Each service will be showcased to your clients on the website.</div>
              <button className="sv-empty-btn" onClick={handleOpenCreate}>
                <i className="fa-solid fa-plus"></i> Add Your First Service
              </button>
            </div>
          ) : filteredServices.length === 0 ? (
            <div className="sv-empty" style={{ padding: '50px 24px' }}>
              <div className="sv-empty-icon-wrap" style={{ width: 72, height: 72, margin: '0 auto 16px' }}>
                <i className="fa-solid fa-magnifying-glass" style={{ fontSize: 28 }}></i>
              </div>
              <div className="sv-empty-title">No matching services</div>
              <div className="sv-empty-text" style={{ maxWidth: 350 }}>We couldn't find any services matching "<strong>{searchTerm}</strong>".</div>
              <button className="sv-btn-cancel" onClick={() => setSearchTerm('')} style={{ background: '#fff' }}>Clear Search</button>
            </div>
          ) : (
            <div className="sv-grid-list">
              {filteredServices.map(service => {
                const page = linkedPage(service.page_id);
                return (
                  <div key={service.id} className="sv-service-card">
                    {service.image
                      ? <img src={service.image} alt={service.title} className="sv-card-img" />
                      : <div className="sv-card-img-placeholder"><i className="fa-solid fa-briefcase"></i></div>
                    }
                    <div className="sv-card-body">
                      <div className="sv-card-name">{service.title}</div>
                      {service.description && (
                        <div className="sv-card-desc">
                          {service.description.length > 90 ? service.description.slice(0, 90) + '…' : service.description}
                        </div>
                      )}
                      {page && (
                        <span className="sv-page-link"><i className="fa-solid fa-link" style={{ fontSize: 11 }}></i>{page.title}</span>
                      )}
                    </div>
                    <div className="sv-card-footer">
                      <span className={`sv-badge ${service.is_active ? 'active' : 'inactive'}`}>
                        <span className="sv-badge-dot"></span>
                        {service.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <div className="sv-card-actions">
                        <button className="sv-act-btn edit" onClick={() => handleOpenEdit(service)} title="Edit">
                          <i className="fa-solid fa-pen-to-square"></i>
                        </button>
                        <button className="sv-act-btn del" onClick={() => confirmDelete(service)} title="Delete">
                          <i className="fa-solid fa-trash-can"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>

      {showModal && (
        <div className="sv-overlay" onClick={() => setShowModal(false)}>
          <div className="sv-drawer" onClick={e => e.stopPropagation()}>

            {/* Drawer Header */}
            <div className="sv-dh">
              <div className="sv-dh-left">
                <div className="sv-dh-icon"><i className="fa-solid fa-briefcase"></i></div>
                <div>
                  <div className="sv-dh-title">{isEditing ? 'Edit Service' : 'New Service'}</div>
                  <div className="sv-dh-sub">{isEditing ? 'Update the service details below.' : 'Fill in the details to add a new service.'}</div>
                </div>
              </div>
              <div className="sv-dh-actions">
                <button className="sv-close" onClick={() => setShowModal(false)} title="Close">
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>
            </div>

            {/* Drawer Body: Image Panel + Form Panel */}
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div className="sv-drawer-body">

                {/* Left: Live Image Preview */}
                <div className="sv-img-panel">
                  <div className="sv-img-banner">
                    {imagePreview
                      ? <img src={imagePreview} alt="Preview" />
                      : (
                        <div className="sv-img-banner-placeholder">
                          <i className="fa-solid fa-image"></i>
                          <span>Hover to upload a service image. It will appear here as a live preview.</span>
                        </div>
                      )
                    }
                    <div className="sv-upload-overlay">
                      <i className="fa-solid fa-camera"></i>
                      <span>{imagePreview ? 'Change Image' : 'Upload Image'}</span>
                      <input type="file" accept="image/*" onChange={handleImageChange} />
                    </div>
                  </div>
                  <div className="sv-img-info">
                    <div className="sv-img-info-title">Visibility</div>
                    <div className="sv-toggle-row" style={{ padding: '0', border: 'none', background: 'transparent' }}>
                      <span className="sv-toggle-label">Service is Active</span>
                      <label className="sv-switch">
                        <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleChange} />
                        <span className="sv-slider"></span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Right: Form Fields */}
                <div className="sv-form-panel">

                  {/* Section: Basic Info */}
                  <div>
                    <div className="sv-section-title"><i className="fa-solid fa-circle-info"></i> Basic Information</div>
                    <div className="sv-float-group">
                      <input type="text" className="sv-float-inp" name="title" value={formData.title} onChange={handleChange} required placeholder=" " />
                      <label className="sv-float-label">Service Title *</label>
                    </div>
                    <div className="sv-float-group">
                      <textarea className="sv-float-inp sv-float-textarea" name="description" value={formData.description} onChange={handleChange} placeholder=" "></textarea>
                      <label className="sv-float-label">Description</label>
                    </div>
                  </div>

                  {/* Section: URL & Order */}
                  <div>
                    <div className="sv-section-title"><i className="fa-solid fa-link"></i> URL & Order</div>
                    <div className="sv-row">
                      <div>
                        <div className="sv-float-group">
                          <input type="text" className="sv-float-inp" name="slug" value={formData.slug} onChange={handleChange} placeholder=" " />
                          <label className="sv-float-label">URL Slug</label>
                        </div>
                        <div className="sv-hint">Auto-generated from title if blank.</div>
                      </div>
                      <div className="sv-float-group">
                        <input type="number" className="sv-float-inp" name="sort_order" value={formData.sort_order} onChange={handleChange} placeholder=" " />
                        <label className="sv-float-label">Sort Order</label>
                      </div>
                    </div>
                  </div>

                  {/* Section: Linked Page */}
                  <div>
                    <div className="sv-section-title"><i className="fa-solid fa-file-lines"></i> Linked Page</div>
                    <div className="sv-float-group">
                      <select className="sv-float-inp sv-float-select" name="page_id" value={formData.page_id} onChange={handleChange}>
                        <option value="">— None —</option>
                        {pages.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                      </select>
                      <label className="sv-float-label" style={{ top: 7, fontSize: '10.5px', color: '#CFA047', fontWeight: 700 }}>Linked Page</label>
                    </div>
                    <div className="sv-hint">Optionally link this service to a page on the site.</div>
                  </div>

                </div>
              </div>

              {/* Drawer Footer */}
              <div className="sv-df">
                <div className="sv-df-hint">
                  <i className="fa-solid fa-circle-info" style={{ marginRight: 6, color: '#CFA047' }}></i>
                  {isEditing ? 'Changes are saved immediately on submit.' : 'The service will appear on your site after saving.'}
                </div>
                <div className="sv-df-btns">
                  <button type="button" className="sv-btn-cancel" onClick={() => setShowModal(false)}>Discard</button>
                  <button type="submit" className="sv-btn-save" disabled={saving}>
                    {saving
                      ? <><i className="fa-solid fa-circle-notch fa-spin"></i> Saving…</>
                      : <><i className="fa-solid fa-floppy-disk"></i> {isEditing ? 'Update Service' : 'Create Service'}</>
                    }
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
      {itemToDelete && (
        <div className="sv-overlay" onClick={() => setItemToDelete(null)} style={{ alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', padding: '28px 24px', borderRadius: '16px', width: '320px', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', animation: 'sv-drop 0.2s ease-out' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#fee2e2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', margin: '0 auto 16px' }}>
              <i className="fa-solid fa-triangle-exclamation"></i>
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>Delete Service</h3>
            <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 24px', lineHeight: 1.5 }}>
              Are you sure you want to delete the service <strong>"{itemToDelete.title}"</strong>? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button style={{ flex: 1, padding: '12px', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', border: 'none', background: '#f1f5f9', color: '#475569' }} onClick={() => setItemToDelete(null)}>Cancel</button>
              <button style={{ flex: 1, padding: '12px', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', border: 'none', background: '#ef4444', color: 'white' }} onClick={executeDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminServices;
